import { useState, useEffect } from "react";
import Layout from "../components/Layout";
import { Link, useNavigate } from "react-router-dom";
import { RiArrowRightLine, RiMedicineBottleLine, RiFilter3Line, RiFlaskLine, RiBuildingLine, RiLeafLine } from "react-icons/ri";
import { MdCompareArrows } from "react-icons/md";
import SponsersAffiliations from "../components/SponsersAffiliations";
import ManufacturerAPI from "../api/manufacturer";
import { useSchemaMicrodata } from "../hooks/useSchemaMicrodata";
import featuredMedicines from "../data/featuredMedicines";
import SearchBar from "../components/SearchBar";
import KEYWORD_ALIASES from "../data/keywordAliases";
import { FiSearch } from "react-icons/fi";

export default function Home() {
  const [manufacturers, setManufacturers] = useState([]);
  const [isLoadingManufacturers, setIsLoadingManufacturers] = useState(true);
  const [searchCategory, setSearchCategory] = useState("all");
  const [activeStep, setActiveStep] = useState(null);
  const navigate = useNavigate();

  const searchCategories = [
    { id: "all", label: "All", icon: RiFilter3Line },
    { id: "tradenames", label: "Brand names", icon: RiFlaskLine },
    { id: "manufacturers", label: "Manufacturers", icon: RiBuildingLine },
    { id: "ingredients", label: "Ingredients", icon: RiLeafLine },
  ];

  useSchemaMicrodata("website", {
    name: "Pharmacy Wiki",
    description:
      "Find affordable generic alternatives to expensive brand-name medicines. Search by drug name and discover equivalent generics from trusted manufacturers.",
    url: window.location.origin,
  });

  useEffect(() => {
    const fetchManufacturers = async () => {
      try {
        setIsLoadingManufacturers(true);
        const data = await ManufacturerAPI.getFeatures();
        setManufacturers(data.results || []);
      } catch (error) {
        console.error("Error fetching manufacturers:", error);
        setManufacturers([]);
      } finally {
        setIsLoadingManufacturers(false);
      }
    };
    fetchManufacturers();
  }, []);

  return (
    <Layout>
      {/* Hero Section */}
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-[#f5f5f7]">
        <div className="container mx-auto px-6 pt-16 pb-16 md:pt-20 md:pb-20 max-md:px-4 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            {/* Left Column: Text + Search */}
            <div className="lg:col-span-7 flex flex-col justify-center text-left max-lg:text-center max-lg:items-center">
              <div>
                <h1
                  className="text-48 max-lg:text-36 max-md:text-28 font-bold text-black leading-[1.15] tracking-tight"
                  style={{ textWrap: "balance" }}
                >
                  Find the{" "}
                  <span className="inline-block text-transparent bg-gradient-title bg-clip-text">
                    right medicine
                  </span>
                  <br className="max-md:hidden" />
                  {" "}at the{" "}
                  <span className="inline-block text-transparent bg-gradient-title bg-clip-text">
                    right price
                  </span>
                </h1>
                <p
                  className="mt-5 max-md:mt-3 text-18 max-md:text-15 font-text text-black/70 max-w-[540px] max-lg:mx-auto max-md:max-w-full leading-relaxed"
                >
                  Look up any brand-name drug to see its{" "}
                  <span className="inline-block text-transparent bg-gradient-title bg-clip-text font-semibold">
                    active ingredients
                  </span>{" "}
                  and discover affordable generic alternatives from verified manufacturers.
                </p>
              </div>

              {/* Search Container with Background */}
              <div 
                className="mt-8 max-md:mt-6 p-8 max-md:p-4 rounded-3xl max-md:rounded-2xl w-full"
                style={{ 
                  backgroundColor: "rgb(207 211 215 / 78%)",
                  height: "fit-content"
                }}
              >
                {/* Search Bar */}
                <div className="w-full">
                  <SearchBar 
                    className="w-full max-w-none" 
                    defaultType={searchCategory}
                  />
                </div>

                {/* Quick examples */}
                <div className="mt-5 max-md:mt-3 flex items-center gap-3 max-md:gap-2 flex-wrap max-lg:justify-center">
                  <span className="text-14 max-md:text-12 font-text text-black/60">Try:</span>
                  {["Cialis", "Lipitor", "sleeping pill", "blood pressure"].map((name) => (
                    <button
                      key={name}
                      onClick={() => {
                        navigate(`/search?q=${encodeURIComponent(name)}&type=${searchCategory}&page=1`);
                      }}
                      className="px-4 py-1.5 max-md:px-3 max-md:py-1 rounded-full text-14 max-md:text-12 font-text font-medium text-black/70 bg-primary/15 hover:bg-white/25 transition-colors"
                    >
                      {name}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column: Doctor Image */}
            <div className="lg:col-span-5 flex justify-center lg:justify-end w-full">
              <div className="relative w-full max-w-[450px] lg:max-w-full h-[260px] sm:h-[320px] md:h-[380px] lg:h-[480px] rounded-3xl overflow-hidden border border-black/5 shadow-sm bg-white">
                <img
                  src="/images/docter_what.png"
                  alt="Friendly pharmacist guiding medicine search"
                  className="w-full h-full object-cover select-none"
                  style={{ objectPosition: "right top" }}
                />
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Search by What It Does (Keyword categories) */}
      <section className="container mx-auto px-6 py-12 max-md:py-8">
        <div className="flex items-baseline justify-between mb-10 max-md:mb-6 max-md:flex-col max-md:gap-3">
          <div>
            <h2 className="text-30 max-md:text-22 font-semibold text-secondary leading-tight">
              Search by what it does
            </h2>
            <p className="mt-2 text-16 font-text text-secondary/70">
              Don't know the name? Search using everyday words instead.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {KEYWORD_ALIASES.slice(0, 8).map((entry) => (
            <KeywordTile key={entry.keyword} entry={entry} />
          ))}
        </div>
      </section>

      {/* Explore Tools (Compare + Ingredients) */}
      <section className="container mx-auto px-6 py-14 max-md:py-10">
        <div className="mb-8 max-md:mb-6">
          <h2 className="text-30 max-md:text-22 font-semibold text-secondary leading-tight">
            More ways to explore
          </h2>
          <p className="mt-2 text-16 font-text text-secondary/70">
            Beyond a simple name search, compare products side by side or dig into active ingredients.
          </p>
        </div>

        <div className="flex flex-col gap-5">
          {/* Compare Feature Highlight */}
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/5 via-white to-primary/3 border border-primary/10 p-8 max-md:p-5">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="flex items-center justify-center size-14 rounded-xl bg-primary/10 flex-shrink-0">
                <MdCompareArrows className="size-7 text-primary" />
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-20 max-md:text-18 font-semibold text-secondary">
                  Compare medicines side by side
                </h3>
                <p className="mt-1.5 text-16 font-text text-secondary/70 max-w-lg">
                  Select up to 4 products and compare their ingredients,
                  composition, manufacturers, and pricing in one view.
                </p>
              </div>
              <Link
                to="/tradenames"
                className="inline-flex items-center gap-2 px-5 py-2.5 text-16 font-text font-medium text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors flex-shrink-0"
              >
                <RiMedicineBottleLine className="size-4" />
                Browse and compare
              </Link>
            </div>
          </div>

          {/* Search by Ingredients Highlight */}
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-green-50 via-white to-green-50/30 border border-green-100 p-8 max-md:p-5">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="flex items-center justify-center size-14 rounded-xl bg-green-100 flex-shrink-0">
                <RiLeafLine className="size-7 text-green-600" />
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-20 max-md:text-18 font-semibold text-secondary">
                  Search by Combine Ingredients
                </h3>
                <p className="mt-1.5 text-16 font-text text-secondary/70 max-w-lg">
                  Select one or more active ingredients and find all products
                  that contain them. Combine ingredients to narrow your search.
                </p>
              </div>
              <Link
                to="/ingredients/search"
                className="inline-flex items-center gap-2 px-5 py-2.5 text-16 font-text font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors flex-shrink-0"
              >
                <RiLeafLine className="size-4" />
                Search by Ingredients
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-secondary/[0.03] py-14 max-md:py-10">
        <div className="container mx-auto px-6">
        <div className="max-w-[980px] mx-auto">
          <div className="text-center mb-10 max-md:mb-8">
            <h2 className="text-30 max-md:text-22 font-bold text-secondary leading-tight">
              How it works
            </h2>
            <p className="mt-3 text-16 font-text text-secondary/60 max-w-[500px] mx-auto">
              Three simple steps to save on your daily medications without compromising quality
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-md:gap-8">
            <HowItWorksStep
              number="1"
              title="Search your medicine"
              description="Enter the brand name you know, a simple keyword like 'sleeping pill', or drop a photo of the packaging."
              isLast={false}
              stepIndex={1}
              activeStep={activeStep}
              setActiveStep={setActiveStep}
            >
              <MiniSearchMockup isActive={activeStep === 1} />
            </HowItWorksStep>
            <HowItWorksStep
              number="2"
              title="See active ingredient"
              description="We show you the chemical name (e.g. Tadalafil) that makes the brand drug work."
              isLast={false}
              stepIndex={2}
              activeStep={activeStep}
              setActiveStep={setActiveStep}
            >
              <TranslateMockup isActive={activeStep === 2} />
            </HowItWorksStep>
            <HowItWorksStep
              number="3"
              title="Find generics & save"
              description="Browse equivalent generic versions from trusted manufacturers at lower cost. Compare side by side."
              isLast={true}
              stepIndex={3}
              activeStep={activeStep}
              setActiveStep={setActiveStep}
            >
              <CostComparisonMockup isActive={activeStep === 3} />
            </HowItWorksStep>
          </div>
        </div>
        </div>
      </section>

      {/* Popular Medicines */}
      <section className="container mx-auto px-6 py-12 max-md:py-8">
        <div className="flex items-baseline justify-between mb-8 max-md:mb-6 max-md:flex-col max-md:gap-3">
          <div>
            <h2 className="text-30 max-md:text-22 font-semibold text-secondary leading-tight">
              Popular medicines
            </h2>
            <p className="mt-2 text-16 font-text text-secondary/70">
              Common brand-name drugs and their generic equivalents
            </p>
          </div>
          <Link
            to="/tradenames"
            className="text-16 font-text font-medium text-primary hover:text-primary/80 transition-colors flex items-center gap-1"
          >
            Browse all medicines
            <RiArrowRightLine className="size-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {featuredMedicines.map((medicine) => (
            <MedicineCard key={medicine.ingredientUri} medicine={medicine} />
          ))}
        </div>
      </section>

      {/* Trusted Manufacturers */}
      <section className="container mx-auto px-6 py-12 max-md:py-8">
        <div className="flex items-baseline justify-between mb-8 max-md:mb-6 max-md:flex-col max-md:gap-3">
          <div>
            <h2 className="text-30 max-md:text-22 font-semibold text-secondary leading-tight">
              Trusted manufacturers
            </h2>
            <p className="mt-2 text-16 font-text text-secondary/70">
              Established pharmaceutical companies producing quality generics
            </p>
          </div>
          <Link
            to="/manufacturers"
            className="text-16 font-text font-medium text-primary hover:text-primary/80 transition-colors flex items-center gap-1"
          >
            View all manufacturers
            <RiArrowRightLine className="size-4" />
          </Link>
        </div>

        {isLoadingManufacturers ? (
          <ManufacturerSkeleton />
        ) : manufacturers.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {manufacturers.slice(0, 8).map((manufacturer, i) => (
              <Link
                key={manufacturer._id || i}
                to={
                  manufacturer.uri
                    ? `/manufacturers/${manufacturer.uri}`
                    : manufacturer.link || "#"
                }
                target={manufacturer.link ? "_blank" : "_self"}
                className="group"
              >
                <div className="flex flex-col h-full items-center p-6 max-md:p-4 bg-white rounded-xl border border-secondary/8 hover:border-primary/20 hover:shadow-lg hover:shadow-primary/5 transition-all duration-200">
                  <img
                    src={manufacturer.logo_url || manufacturer.image}
                    alt={manufacturer.name}
                    className="w-16 h-16 max-md:w-12 max-md:h-12 object-contain mb-4"
                    onError={(e) => {
                      e.target.src =
                        "https://imageplaceholder.net/300x150/f3f4f6/131313?text=Logo";
                    }}
                  />
                  <span className="text-16 max-md:text-14 font-text text-secondary text-center leading-snug group-hover:text-primary transition-colors">
                    {manufacturer.display_name ?? manufacturer.name}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <p className="text-16 font-text text-secondary/70 text-center py-12">
            Unable to load manufacturers. Please try refreshing the page.
          </p>
        )}
      </section>

      <SponsersAffiliations />
    </Layout>
  );
}

/* ─── Sub-components ─────────────────────────────────────────────────────── */

function KeywordTile({ entry }) {
  const navigate = useNavigate();

  return (
    <button
      onClick={() =>
        navigate(
          `/search?q=${encodeURIComponent(entry.keyword)}&type=all&page=1`
        )
      }
      className="group flex flex-col items-start p-4 max-md:p-3 bg-white rounded-xl border border-secondary/8 hover:border-primary/20 hover:shadow-lg hover:shadow-primary/5 transition-all duration-200 text-left"
    >
      <span className="flex justify-between w-full text-16 font-text font-semibold text-secondary group-hover:text-primary transition-colors">
        {entry.keyword.charAt(0).toUpperCase() + entry.keyword.slice(1)}
        <FiSearch  className="size-5 text-black/25 group-hover:text-primary "/>
      </span>
      <span className="mt-1 text-14 font-text text-secondary/70 leading-snug">
        {entry.description}
      </span>
      <span className="mt-2 text-14 font-text text-primary/70">
        {entry.examples.slice(0, 2).map((e) => e.name).join(", ")}...
      </span>
    </button>
  );
}

function HowItWorksStep({ number, title, description, isLast, stepIndex, activeStep, setActiveStep, children }) {
  const isActive = activeStep === stepIndex;
  
  return (
    <div 
      className="relative flex flex-col justify-between p-6 bg-white rounded-2xl border transition-all duration-300 select-none z-10 animate-fadeIn"
      style={{
        borderColor: isActive ? "rgba(11, 132, 254, 0.35)" : "rgba(29, 29, 31, 0.08)",
        transform: isActive ? "translateY(-6px)" : "translateY(0)",
        boxShadow: isActive ? "0 20px 25px -5px rgba(11, 132, 254, 0.06), 0 10px 10px -5px rgba(11, 132, 254, 0.03)" : "none"
      }}
      onMouseEnter={() => setActiveStep(stepIndex)}
      onMouseLeave={() => setActiveStep(null)}
    >
      {/* Horizontal connector line on desktop */}
      {!isLast && (
        <div 
          className="absolute top-[44px] left-[85%] w-[30%] h-[2px] border-t-2 border-dashed hidden md:block transition-all duration-300 z-0 pointer-events-none"
          style={{
            borderColor: activeStep !== null && activeStep > stepIndex ? "#0B84FE" : "rgba(29, 29, 31, 0.08)",
            borderStyle: activeStep !== null && activeStep > stepIndex ? "solid" : "dashed"
          }}
        />
      )}
      
      {/* Vertical connector line on mobile */}
      {!isLast && (
        <div
          className="absolute left-[44px] top-[64px] bottom-[-32px] w-[2px] transition-all duration-300 md:hidden z-0 pointer-events-none"
          aria-hidden="true"
          style={{
            backgroundColor: activeStep !== null && activeStep > stepIndex ? "#0B84FE" : "rgba(29, 29, 31, 0.08)"
          }}
        />
      )}
      
      {/* Content */}
      <div className="flex flex-col gap-4">
        {/* Step Badge & Title */}
        <div className="flex items-center gap-4 relative z-10">
          <div 
            className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-xl font-bold text-16 transition-all duration-300 shadow-sm"
            style={{
              backgroundColor: isActive ? "#0B84FE" : "rgba(11, 132, 254, 0.1)",
              color: isActive ? "#ffffff" : "#0B84FE",
              transform: isActive ? "scale(1.1) rotate(5deg)" : "scale(1)"
            }}
          >
            {number}
          </div>
          <h3 className="text-18 font-bold text-secondary leading-snug">
            {title}
          </h3>
        </div>

        {/* Description */}
        <p className="text-15 font-text text-secondary/70 leading-relaxed min-h-[66px] ml-14 md:ml-0">
          {description}
        </p>
      </div>

      {/* Embedded visual component/mockup */}
      <div 
        className="mt-6 w-auto md:w-full bg-secondary/[0.02] border rounded-2xl p-4 overflow-hidden h-[130px] flex items-center justify-center transition-all duration-300 ml-14 md:ml-0"
        style={{
          borderColor: isActive ? "rgba(11, 132, 254, 0.18)" : "rgba(29, 29, 31, 0.04)"
        }}
      >
        {children}
      </div>
    </div>
  );
}

/* ─── Visual Mockups for How It Works Steps ────────────────────────────── */

function MiniSearchMockup({ isActive }) {
  const words = ["Cialis", "Lipitor", "sleeping pill"];
  const [wordIndex, setWordIndex] = useState(0);
  const [currentText, setCurrentText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    let timer;
    const fullWord = words[wordIndex];
    
    if (isDeleting) {
      timer = setTimeout(() => {
        setCurrentText(fullWord.substring(0, currentText.length - 1));
      }, 60);
    } else {
      timer = setTimeout(() => {
        setCurrentText(fullWord.substring(0, currentText.length + 1));
      }, 120);
    }

    if (!isDeleting && currentText === fullWord) {
      timer = setTimeout(() => setIsDeleting(true), 1500);
    } else if (isDeleting && currentText === "") {
      setIsDeleting(false);
      setWordIndex((prev) => (prev + 1) % words.length);
    }

    return () => clearTimeout(timer);
  }, [currentText, isDeleting, wordIndex]);

  return (
    <div className={`w-full max-w-[240px] bg-white rounded-xl border p-3 flex items-center gap-2 shadow-sm transition-all duration-300 ${
      isActive ? "border-primary/25 shadow-md shadow-primary/5 scale-105" : "border-secondary/10"
    }`}>
      <FiSearch className={`size-4 flex-shrink-0 transition-colors ${isActive ? "text-primary" : "text-secondary/40"}`} />
      <span className="text-14 font-text text-secondary/80 flex-1 relative min-h-[20px] flex items-center overflow-hidden whitespace-nowrap">
        {currentText}
        <span className="inline-block w-[2px] h-[14px] bg-primary ml-0.5 animate-pulse" />
      </span>
      <span className={`text-11 px-2 py-0.5 rounded-md font-medium transition-colors ${
        isActive ? "bg-primary text-white" : "bg-secondary/5 text-secondary/60"
      }`}>Search</span>
    </div>
  );
}

function TranslateMockup({ isActive }) {
  return (
    <div className="w-full max-w-[240px] flex items-center justify-between gap-2.5">
      {/* Brand Box */}
      <div className={`flex flex-col items-center bg-red-50/50 border rounded-xl p-2.5 flex-1 transition-all duration-300 ${
        isActive ? "border-red-200/80 bg-red-50/80 -translate-y-0.5 shadow-sm scale-105" : "border-secondary/10"
      }`}>
        <span className="text-10 font-bold uppercase text-red-500 tracking-wider mb-0.5">Brand</span>
        <span className="text-14 font-bold text-secondary">Cialis</span>
      </div>

      {/* Arrow / Connection */}
      <div className="flex flex-col items-center justify-center flex-shrink-0">
        <RiArrowRightLine className={`size-5 transition-all duration-300 ${
          isActive ? "text-primary translate-x-0.5 scale-110" : "text-secondary/30"
        }`} />
        <span className={`text-10 font-medium transition-colors ${
          isActive ? "text-primary font-semibold animate-pulse" : "text-secondary/40"
        }`}>Active</span>
      </div>

      {/* Generic Box */}
      <div className={`flex flex-col items-center bg-green-50/50 border rounded-xl p-2.5 flex-1 transition-all duration-300 ${
        isActive ? "border-green-200 bg-green-50 -translate-y-0.5 shadow-sm scale-105" : "border-secondary/10"
      }`}>
        <span className="text-10 font-bold uppercase text-green-600 tracking-wider mb-0.5">Generic</span>
        <span className="text-14 font-bold text-green-700">Tadalafil</span>
      </div>
    </div>
  );
}

function CostComparisonMockup({ isActive }) {
  return (
    <div className={`w-full max-w-[240px] bg-white rounded-xl border p-3 flex flex-col gap-2 shadow-sm transition-all duration-300 ${
      isActive ? "border-primary/25 shadow-md shadow-primary/5 scale-105" : "border-secondary/10"
    }`}>
      <div className="flex justify-between items-center text-11 text-secondary/50 font-medium">
        <span>Generics from India</span>
        <span className={`text-green-600 font-bold bg-green-50 px-1.5 py-0.5 rounded transition-all duration-300 ${isActive ? "scale-105" : ""}`}>Save ~90%</span>
      </div>
      
      <div className="space-y-2">
        {/* Generic 1 */}
        <div className="flex items-center justify-between text-11 border-b border-secondary/5 pb-1.5">
          <div className="flex flex-col">
            <span className="font-semibold text-secondary">Cipla Ltd.</span>
            <span className="text-10 text-secondary/40">FDA Approved Facility</span>
          </div>
          <div className="text-right">
            <span className="font-bold text-green-600">$0.85/tab</span>
          </div>
        </div>

        {/* Generic 2 */}
        <div className="flex items-center justify-between text-11">
          <div className="flex flex-col">
            <span className="font-semibold text-secondary">Aurobindo Pharma</span>
            <span className="text-10 text-secondary/40">Verified Manufacturer</span>
          </div>
          <div className="text-right">
            <span className="font-bold text-green-600">$0.78/tab</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function MedicineCard({ medicine }) {
  return (
    <Link
      to={`/ingredients/${medicine.ingredientUri}`}
      className="group block"
    >
      <div className="flex h-full items-center justify-between p-6 max-md:p-4 bg-white rounded-xl border border-secondary/8 hover:border-primary/20 hover:shadow-lg hover:shadow-primary/5 transition-all duration-200">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 max-md:gap-2 flex-wrap">
            <span className="text-20 max-md:text-18 font-semibold text-secondary group-hover:text-primary transition-colors">
              {medicine.brandName}
            </span>
            <span className="text-16 font-text text-secondary/60">
              →
            </span>
            <span className="text-18 max-md:text-16 font-text font-medium text-primary/80">
              {medicine.genericName}
            </span>
          </div>
          <p className="mt-2 text-16 max-md:text-14 font-text text-secondary/70 leading-relaxed">
            {medicine.description}
          </p>
        </div>
        <div className="flex-shrink-0 ml-5 max-md:ml-3">
          <span className="inline-flex items-center px-4 py-2 max-md:px-3 max-md:py-1.5 rounded-full bg-primary/8 text-primary text-14 font-text font-medium whitespace-nowrap group-hover:bg-primary group-hover:text-white transition-colors">
            Find generics
          </span>
        </div>
      </div>
    </Link>
  );
}

function ManufacturerSkeleton() {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
      {Array.from({ length: 8 }).map((_, i) => (
        <div
          key={i}
          className="flex flex-col items-center p-6 bg-white rounded-xl border border-secondary/8 animate-pulse"
        >
          <div className="w-16 h-16 rounded-lg bg-secondary/8 mb-4" />
          <div className="w-20 h-4 rounded bg-secondary/8" />
        </div>
      ))}
    </div>
  );
}
